import { createAsyncThunk, createSlice, original } from "@reduxjs/toolkit";
import { type RootState } from "../../app/store";
import { JOB_SERVER_URL } from "../../config";
import http from "../../net/http-common";
import { setTaskStatus } from "../../components/layout/SnakebarSlice";
import { getSingleSmileData } from "./leaderApis";
import { isAxiosError } from "axios";
import { setGlobalMsgAlerts } from "../../components/GloablMsgAlerts/globalMsgAlertsSlice";

interface stateType {
  properties: {
    data: any;
    loading: boolean;
    jobId : string;
    singleSmileData: any;
  };
}

const initialState: stateType = {
  properties: {
    data: null,
    loading: false,
    jobId : "",
    singleSmileData: null,
  },
};

export const fetchLeaderReinventProperties = createAsyncThunk(
  "leaderReinventProperties/fetchLeaderReinventProperties",
  async (task_id: string, { dispatch }) => {
    return http
      .get(
        `${JOB_SERVER_URL}/reinvent/results?task_id=${task_id}&as_file=${false}`
      )
      .then((response) => response.data)
      .catch((error) => {
        let msg: string = "";
        if (isAxiosError(error)) {
          msg = error.response?.data?.detail?.message;
        }
        dispatch(
          setGlobalMsgAlerts({
            msg: msg,
            isAlert: true,
            color: "error",
          })
        );
      });
  }
);

export const fetchSingleSmileData = createAsyncThunk(
  "leaderReinventProperties/fetchSingleSmileData",
  async (
    {
      smileName,
      relevantProperty,
      moleculesPerLigand,
      optimization,
    }: {
      smileName: string;
      relevantProperty: string;
      moleculesPerLigand: number;
      optimization: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(
        setTaskStatus({
          status: "pending",
          varient: "info",
          msg: "Leader models running..",
          url: "",
          autoHideTime: 5000,
        })
      );
      const response = await getSingleSmileData({
        smileName,
        relevantProperty,
        moleculesPerLigand,
        optimization,
      });

      dispatch(
        setTaskStatus({
          status: "success",
          varient: "success",
          msg: "Leader request finished successfully.",
          url: "/leader/leader/visualize",
          autoHideTime: 3600000,
        })
      );
      return response.data;
    } catch (error: any) {
      let msg = "";
      if (isAxiosError(error)) {
        msg = error.response?.data?.detail?.message;
      }
      dispatch(
        setTaskStatus({
          status: "error",
          varient: "error",
          msg: msg || "Your request failed",
          url: "",
          autoHideTime: 8000,
        })
      );
      return rejectWithValue(msg || "Failed to fetch data.");
    }
  }
);

const leaderReinventPropertiesSlice = createSlice({
  name: "leaderReinventProperties",
  initialState,
  reducers: {
    setSingleSmileData(state, action) {
      state.properties.singleSmileData = action.payload;
    },
    updateSmilesById(state, action) {
      const { id } = action.payload;
      const sliceData = original(state.properties);
      state.properties.singleSmileData = {
        ...sliceData.singleSmileData,
        id: id,
        data: sliceData.data[sliceData.singleSmileData.evaluationIds[id] - 1],
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchLeaderReinventProperties.fulfilled,
        (state: stateType, action: any) => {
          state.properties.data = action.payload.results;
          state.properties.jobId = action.payload.task_id;
          state.properties.loading = false;
        }
      )
      .addCase(fetchLeaderReinventProperties.rejected, (state, action: any) => {
        state.properties.loading = false;
      })
      .addCase(fetchLeaderReinventProperties.pending, (state) => {
        state.properties.loading = true;
      })
      .addCase(
        fetchSingleSmileData.fulfilled,
        (state: stateType, action: any) => {
          state.properties.data = action.payload;
          state.properties.loading = false;
        }
      )
      .addCase(fetchSingleSmileData.rejected, (state, action: any) => {
        state.properties.loading = false;
      })
      .addCase(fetchSingleSmileData.pending, (state) => {
        state.properties.loading = true;
      });
  },
});

export const { updateSmilesById, setSingleSmileData } =
  leaderReinventPropertiesSlice.actions;
export const leaderReinventProperties = (state: RootState) =>
  state.leaderReinventProperty;
export default leaderReinventPropertiesSlice.reducer;
